import React, { useMemo } from "react";
import NavbarDoctorOffice from "../../../NavbarDoctorOffice";
import { Card, CardBody, CardFooter, Col, Input, Label, Row } from "reactstrap";
import ReactTable from "@dhn/components/molecules/data-table";
import useActions from "./useActions";
import ReactFormat from "react-number-format";
import DatePicker from "react-datepicker";
import { Button } from "reactstrap";
import ModalIssuanceCreditNote from "../../modals/ModalIssuanceCreditNote";
import DoctorsOfficePage from "../../../components/DoctorsOfficePage";
import "../../monthly-closing-motion.css";

export default function MonthlyClosingDetail({
  profile,
  user,
  center,
  dataDoctor,
  history,
}) {
  const [{ state, loadingMonthlyClosing, loadingClosedDetail }, actions] =
    useActions({ dataDoctor });

  const {
    sales,
    selectedDate,
    selectedBilling,
    isOpenCreditNoteModal,
    isReprinting,
    monthlyClosing,
  } = state;
  const {
    onOpenCreditNote,
    onCloseCreditNote,
    handleReprintNcfPrincipal,
    handleReprintAffiliateAnnex,
    handleReprintBranchAnnex,
    refetchClosedDetail,
  } = actions;

  const isTableLoading = loadingMonthlyClosing || loadingClosedDetail;

  const totals = useMemo(
    () =>
      (sales || []).reduce(
        (acc, item) => ({
          invoices: acc.invoices + Number(item?.invoices || 0),
          contractedValue:
            acc.contractedValue + Number(item?.contractedValue || 0),
          coverageValue: acc.coverageValue + Number(item?.coverageValue || 0),
          availableValue:
            acc.availableValue + Number(item?.availableValue || 0),
        }),
        { invoices: 0, contractedValue: 0, coverageValue: 0, availableValue: 0 },
      ),
    [sales],
  );

  const col = useMemo(
    () => [
      {
        Header: "Aseguradora",
        Footer: () => <strong>SUM</strong>,
        Cell: ({ original }) => `${original?.insurerName || "No Asegurado"}`,
      },
      {
        Header: "No. Facturas",
        accessor: "invoices",
        className: "text-center",
        Footer: () => <strong>{totals.invoices}</strong>,
        footerClassName: "text-center",
      },
      {
        Header: "NCF Generado",
        dataType: "fiscalNumber",
        accessor: "ncf",
        className: "text-center",
        Cell: ({ original }) => `${original?.ncf || ""}`,
        footerClassName: "text-center",
        Footer: () => "",
      },
      {
        Header: "Valor Contratado",
        className: "text-right",
        Footer: () => (
          <ReactFormat
            style={{ textAlign: "right", display: "block", fontWeight: 700 }}
            value={totals.contractedValue}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
        footerClassName: "text-right",
        Cell: ({ original }) => (
          <ReactFormat
            style={{ textAlign: "right", display: "block" }}
            value={original?.contractedValue || "00.00"}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
      },
      {
        Header: "Cobertura",
        className: "text-right",
        Footer: () => (
          <ReactFormat
            style={{ textAlign: "right", display: "block", fontWeight: 700 }}
            value={totals.coverageValue}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
        footerClassName: "text-right",
        Cell: ({ original }) => (
          <ReactFormat
            style={{ textAlign: "right", display: "block" }}
            value={original?.coverageValue || "00.00"}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
      },
      {
        Header: "Disponible",
        className: "text-right",
        Footer: () => (
          <ReactFormat
            style={{ textAlign: "right", display: "block", fontWeight: 700 }}
            value={totals.availableValue}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
        footerClassName: "text-right",
        Cell: ({ original }) => (
          <ReactFormat
            style={{ textAlign: "right", display: "block" }}
            value={original?.availableValue || "00.00"}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
      },
      {
        Header: "Opciones",
        width: 480,
        Footer: "",
        Cell: ({ original }) => (
          <div>
            <Button
              color="secondary"
              className="btn-color-gray card-header-action"
              disabled={isReprinting}
              onClick={() => handleReprintNcfPrincipal(original)}
            >
              <i className="fa fa-undo"></i>
              {" Re-imprimir NCF"}
            </Button>
            <Button
              color="primary"
              className="card-header-action mx-1"
              disabled={isReprinting}
              onClick={() => handleReprintAffiliateAnnex(original)}
            >
              <i className="fa fa-file-pdf-o"></i>
              {" Anexo 1"}
            </Button>
            <Button
              color="primary"
              className="card-header-action mr-1"
              disabled={isReprinting}
              onClick={() => handleReprintBranchAnnex(original)}
            >
              <i className="fa fa-file-pdf-o"></i>
              {" Anexo 2"}
            </Button>
            <Button
              className="btn-green card-header-action"
              disabled={isReprinting}
              onClick={() => onOpenCreditNote(original)}
            >
              <i className="fa fa-credit-card"></i>
              {" Emitir Nota Credito"}
            </Button>
          </div>
        ),
      },
    ],
    [
      handleReprintAffiliateAnnex,
      handleReprintBranchAnnex,
      handleReprintNcfPrincipal,
      isReprinting,
      onOpenCreditNote,
      totals,
    ],
  );

  return (
    <div className="animated fadeIn monthly-closing-view-enter">
      <NavbarDoctorOffice
        profile={profile}
        user={user}
        center={center}
        dataDoctor={dataDoctor}
      />

      <DoctorsOfficePage icon="fa-calculator" title="Cierre Mensual" className="monthly-closing-card-enter">
        <Row form>
          <Col md={3}>
            <Label>Centro:</Label>
            <Input value={center} disabled />
          </Col>
          <Col md={4}>
            <Label>Médico</Label>
            <Input disabled value={`${user.firstName} ${user.lastName}`} />
          </Col>
          <Col md={1} className="d-flex flex-column">
            <Label>Mes</Label>
            <DatePicker
              className="form-control text-center"
              disabled
              selected={selectedDate}
              dateFormat="MM-yyyy"
              showMonthYearPicker
            />
          </Col>
        </Row>
        <Row form className="my-2">
          <Col>
            <Label className="fw-bold border-bottom">
              Ventas por Seguro
            </Label>{" "}
          </Col>
        </Row>
        <Card>
          <CardBody>
            <Row form>
              <Col className="monthly-closing-table-enter">
                <ReactTable
                  defaultPageSize={10}
                  data={sales}
                  loading={isTableLoading}
                  loadingText="Cargando datos..."
                  noDataText="No hay datos para mostrar"
                  columns={col}
                />
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="d-inline">
            <Row form>
              <Col>
                <Button
                  color="danger"
                  className="btn-color-danger"
                  onClick={() => history.goBack()}
                >
                  <i className="fa fa-times"></i>
                  {" Cerrar"}
                </Button>
              </Col>
            </Row>
          </CardFooter>
        </Card>
      </DoctorsOfficePage>

      <ModalIssuanceCreditNote
        isOpen={isOpenCreditNoteModal}
        toggle={onCloseCreditNote}
        selectedBilling={selectedBilling}
        monthlyClosingId={monthlyClosing?.id}
        monthlyClosing={monthlyClosing}
        onSaved={refetchClosedDetail}
      />
    </div>
  );
}
